import React, { useState, useEffect, useRef } from 'react';
import expandIcon from '../assets/images/expand.png';
import collapseIcon from '../assets/images/expand.png'; // Update to a different collapse icon if needed
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Exporting from 'highcharts/modules/exporting';
import Modal from 'react-bootstrap/Modal';

const optionsColumnChart = (vectorAnalytics, year) => {
	const colors = [
		'#F2817C', '#0E6BD0', '#DB528B', '#59BDE9', '#FF9602',
		'#81858D', '#27737D', '#D5B2E7', '#4CAF50', '#FF5733',
	];
	const admDesc = { ADM0: 'Country', ADM1: 'Province', ADM2: 'District' };

	let seriesData = [];
	let totalCount = 0;
	let subtitleText = '';

	const hasDrilldownData = vectorAnalytics?.drilldownChart?.length > 0;
	if (hasDrilldownData && year !== null) {
		const yearData = vectorAnalytics.drilldownChart
			.map((item) => {
				const yearEntry = item.data.find(([y]) => y === year);
				return yearEntry && typeof yearEntry[1] === 'number' && !isNaN(yearEntry[1])
					? [item.id, yearEntry[1]]
					: null;
			})
			.filter((entry) => entry !== null && entry[0] && entry[1] !== null && entry[1] !== undefined)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 10);

		console.log(`Year: ${year}, YearData:`, yearData);

		seriesData = yearData.length > 0
			? yearData.map(([name, value], index) => ({
				name: name || 'Unknown',
				y: Number(value),
				color: colors[index % colors.length],
			}))
			: [{ name: 'No Data', y: 0, color: colors[0] }];

		totalCount = yearData.length > 0 ? yearData.reduce((sum, [, value]) => sum + Number(value), 0) : 0;
	} else if (vectorAnalytics?.columnChart?.length > 0) {
		seriesData = vectorAnalytics.columnChart.map((loc, index) => ({
			name: loc.name || 'Unknown',
			y: Number(loc.value),
			color: colors[index % colors.length],
		}));

		totalCount = vectorAnalytics.columnChart.reduce((sum, loc) => sum + Number(loc.value), 0);
	} else {
		seriesData = [{ name: 'No Data', y: 0, color: colors[0] }];
	}

	return {
		chart: {
			type: 'column',
			animation: false,
			marginRight: 50,
			marginTop: 40,
			height: 450,
		},
		title: { text: null },
		subtitle: {
			useHTML: true,
			text: subtitleText,
			floating: true,
			align: 'right',
			verticalAlign: 'bottom',
			x: 10,
			y: 20,
			zIndex: 1000,
		},
		xAxis: { type: 'category' },
		yAxis: {
			opposite: true,
			tickPixelInterval: 150,
			title: { text: vectorAnalytics?.units || '' },
		},
		legend: { enabled: false },
		plotOptions: {
			series: {
				animation: false,
				groupPadding: 0,
				pointPadding: 0.1,
				borderWidth: 0,
				colorByPoint: true,
				dataLabels: { enabled: true, format: '{point.y:.1f}' },
			},
		},
		tooltip: {
			headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
			pointFormat: '<span style="color:{point.color}">{point.name}</span>: <b>{point.y:.2f}</b><br/>',
		},
		series: [
			{
				name: `${admDesc[`ADM${vectorAnalytics?.admX}`] || 'Region'}: ${vectorAnalytics?.layerName || 'Data'}`,
				data: seriesData,
			},
		],
		exporting: {
			enabled: false,
			filename: vectorAnalytics?.layerName || 'chart',
			buttons: {
				contextButton: {
					menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG'],
					y: -15,
					x: -10,
					zIndex: 1000,
				},
			},
		},
		credits: { enabled: false },
	};
};

const Product2Legend = ({
	admX,
	currentMapView,
	currentHazard,
	layerLegend,
	vectorAnalytics,
	sideBarOpen,
	onOpacityChange,
}) => {
	const admDesc = { ADM0: 'countries', ADM1: 'provinces', ADM2: 'districts' };
	const [isExpanded, setIsExpanded] = useState(true);
	const [show1, setShow1] = useState(false);
	const [year, setYear] = useState(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [opacityMap, setOpacityMap] = useState({ legend: 0.75 }); // Single opacity for the entire legend
	const chartRef = useRef(null);

	// Determine the range of years from drilldownChart
	const years = Array.from(
		new Set(
			vectorAnalytics?.drilldownChart?.flatMap((item) => item.data.map(([y]) => y)).sort((a, b) => a - b),
		),
	).filter((y) => typeof y === 'number' && !isNaN(y));
	const startYear = years[0] || 2001;
	const endYear = years[years.length - 1] || 2024;

	// Initialize opacity map when map view or legend data changes
	useEffect(() => {
		if (currentMapView === 'Vector') {
			setOpacityMap({ legend: 0.75 }); // Default for vector layers
		} else if (currentMapView === 'Raster') {
			setOpacityMap({ legend: 0.8 }); // Default for raster layer
		} else {
			setOpacityMap({ legend: 0.75 }); // Default fallback
		}
	}, [currentMapView, layerLegend]);

	// Handle opacity change for the entire legend
	const handleOpacityChangeInternal = (value) => {
		const newOpacity = parseFloat(value);
		setOpacityMap({ legend: newOpacity });
		// Pass the opacity to the parent for all legend items
		if (currentMapView === 'Vector' && layerLegend?.legendData?.length > 0) {
			layerLegend.legendData.forEach((item) => {
				if (item.color) {
					onOpacityChange(item.color, newOpacity);
				}
			});
		} else if (currentMapView === 'Raster') {
			onOpacityChange('raster', newOpacity);
		}
	};

	// Set initial year when drilldownChart is available
	useEffect(() => {
		if (vectorAnalytics?.drilldownChart?.length > 0 && years.length > 0 && year === null) {
			setYear(endYear);
		}
	}, [vectorAnalytics, years, endYear, year]);

	const handleClose1 = () => {
		pause();
		setShow1(false);
		setYear(endYear); // Reset to latest year
	};

	const handleShow1 = () => {
		setShow1(true);
		if (years.length > 0) {
			setYear(endYear);
		}
	};

	const toggleContent = () => setIsExpanded(!isExpanded);

	const formatNumber = (n) =>
		n >= 1e9 ? (n / 1e9).toFixed(1) + 'B' :
			n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' :
				n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' :
					n < 1 ? Number(n).toFixed(1) :
						Math.round(n).toString();

	const play = () => {
		if (years.length === 0) {
			console.warn('No years available to play');
			return;
		}
		setIsPlaying(true);
		setYear(startYear);
		if (chartRef.current) {
			chartRef.current.chart.sequenceTimer = setInterval(() => {
				setYear((prevYear) => {
					const newYear = prevYear + 1;
					if (newYear > endYear) {
						pause();
						return endYear;
					}
					return newYear;
				});
			}, 1500);
		}
	};

	const pause = () => {
		if (chartRef.current?.chart.sequenceTimer) {
			clearInterval(chartRef.current.chart.sequenceTimer);
			chartRef.current.chart.sequenceTimer = undefined;
		}
		setIsPlaying(false);
	};

	const handleButtonClick = () => {
		if (isPlaying) {
			pause();
		} else {
			play();
		}
	};

	const handleRangeChange = (event) => {
		const newYear = parseInt(event.target.value, 10);
		if (!isNaN(newYear)) {
			setYear(newYear);
			pause();
		}
	};

	useEffect(() => {
		if (chartRef.current && vectorAnalytics && (vectorAnalytics.drilldownChart?.length > 0 || vectorAnalytics.columnChart?.length > 0)) {
			if (vectorAnalytics.drilldownChart?.length > 0 && year !== null && years.includes(year)) {
				console.log('Updating chart for year:', year);
				try {
					chartRef.current.chart.update(optionsColumnChart(vectorAnalytics, year), true, false);
				} catch (error) {
					console.error('Error updating chart:', error);
				}
			} else if (vectorAnalytics.columnChart?.length > 0) {
				console.log('Updating chart with columnChart data');
				try {
					chartRef.current.chart.update(optionsColumnChart(vectorAnalytics, null), true, false);
				} catch (error) {
					console.error('Error updating chart:', error);
				}
			}
		}
	}, [year, vectorAnalytics, years]);

	const legendComponent = (
		<>
			{layerLegend?.valueKey && (
				<h3 className="legend-title">
					{layerLegend.valueKey}
					{layerLegend.units ? ` (${layerLegend.units})` : ''}
				</h3>
			)}
			{currentMapView === 'Raster' && layerLegend?.legendData?.length > 0 && (
				<div style={{ width: 200, margin: "0 auto" }}>
					{/* Color Bar */}
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						{layerLegend.legendData.map((a, i) => (
							<div
								key={i}
								style={{
									backgroundColor: a.color,
									flex: 1,              // auto width, fills evenly
									height: 12,
								}}
							/>
						))}
					</div>
					<div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginTop: 2 }}>
						<div>{formatNumber(layerLegend.legendData[0].label)}</div>
						<div>{formatNumber(layerLegend.legendData[layerLegend.legendData.length - 1].label)}</div>
					</div>
				</div>
			)}

			{currentMapView === 'Vector' && layerLegend?.legendData?.length > 0 && (
				<>
					{layerLegend.staticLegend && (
						<>
							{layerLegend.legendData
								.sort((a, b) => (a.min > b.min ? -1 : 0))
								.map((a, i) => (
									<div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
										<div
											style={{
												width: '20px',
												height: '11px',
												backgroundColor: a.color,
												border: '1px solid #999',
												marginRight: '8px',
											}}
										/>
										<span style={{ fontSize: '11px', color: '#333' }}>
											{a.label} ({a.max === null || a.max === 'inf' ? `> ${a.min}` : `${a.min}-${a.max}`})
										</span>
									</div>
								))}
						</>
					)}


					{!layerLegend.staticLegend && layerLegend.legendData?.length > 0 && (
						<div style={{ width: 200, margin: "0 auto" }}>
							{/* Color Bar */}
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								{layerLegend.legendData.map((a, i) => (
									<div
										key={i}
										style={{
											backgroundColor: a.color,
											flex: 1,   // auto distribute across 120px
											height: 12,
										}}
									/>
								))}
							</div>
							<div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginTop: 2 }}>
								<div>{formatNumber(layerLegend.legendData[0].max)}</div>
								<div>{formatNumber(layerLegend.legendData[layerLegend.legendData.length - 1].max)}</div>
							</div>
						</div>
					)}

				</>
			)}
			{/* Single opacity slider for the entire legend */}
			<div style={{ margin: '10px 0 10px 10px' }}>
				<input
					className="ml-2"
					type="range"
					min="0"
					max="1"
					step="0.1"
					value={opacityMap.legend ?? (currentMapView === 'Raster' ? 0.8 : 0.75)}
					onChange={(e) => handleOpacityChangeInternal(e.target.value)}
					style={{
						width: '100px',
						height: '5px',
						borderRadius: '10px',
						appearance: 'none',
						background: `linear-gradient(to right, #105586 ${(opacityMap.legend ?? (currentMapView === 'Raster' ? 0.8 : 0.75)) * 100}%, #ccc 0%)`,
					}}
				/>
				<span style={{ fontSize: '10px', fontWeight: '600', marginLeft: '10px' }}>
					{(opacityMap.legend ?? (currentMapView === 'Raster' ? 0.8 : 0.75)).toFixed(1)}
				</span>
			</div>
		</>
	);

	const boxExpandToggler = (
		<div>
			<button
				onClick={toggleContent}
				className="toggle-button"
				style={{ background: 'none', border: 'none', cursor: 'pointer' }}
			>
				<img
					loading="lazy"
					src={isExpanded ? collapseIcon : expandIcon}
					alt={isExpanded ? 'Collapse' : 'Expand'}
					className="toggle-icon"
				/>
			</button>
		</div>
	);

	return (
		<>
			<div
				className="card legendCard one border-0"
				style={{ position: 'fixed', bottom: '10px', right: sideBarOpen ? '0px' : '0px', zIndex: 1000 }}
			>
				{!isExpanded && (
					<div className="card-header px-0 pb-1 pt-1 border-0 d-flex mb-0 align-items-center">
						<div className="d-flex justify-content-start">
							<button className="tab-button active" style={{ width: vectorAnalytics ? 'auto' : 'auto' }}>
								<span>
									<img
										loading="lazy"
										alt="legend"
										src={vectorAnalytics ? '../assets/images/layers1.png' : '../assets/images/legend.svg'}
									/>
								</span>{' '}
							</button>
						</div>
						{boxExpandToggler}
					</div>
				)}
				{isExpanded && (
					<>
						<div style={{ display: 'flex', justifyContent: 'flex-end', zIndex: 1100, position: 'relative' }}> {/* marginBottom: '-30px', */}
							{boxExpandToggler}
						</div>
						<div className="gradient-legend" style={{ padding: '10px', zIndex: 1000 }}>
							{legendComponent}
						</div>
					</>
				)}
			</div>

			<div
				className="card legendCard border-0"
				style={{ position: 'absolute', right: '8px', top: '75px', height: 'max-content', borderRadius: '4px' }}
			>
				{vectorAnalytics && (
					<div className="card-header p-0 border-0 d-flex mb-0 align-items-center">
						<div>
							<button
								className="tab-button"
								onClick={handleShow1}
								style={{ background: 'noneVadim', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
							>
								<span>
									<img loading="lazy" alt="analysis" src="../assets/images/analysis.svg" />
								</span>{' '}
								Analytics
							</button>
						</div>
					</div>
				)}
			</div>

			<Modal size="md" aria-labelledby="contained-modal-title-vcenter" centered show={show1} onHide={handleClose1}>
				<Modal.Body>
					<div className="row">
						<div className="col-sm-12 col-md-12 col-lg-12">
							<div className="analysisBox">
								<div className="d-flex justify-content-between align-items-center">
									<div>
										<h4>
											<img src="../assets/images/analytics.png" className="me-1" />
											Analytics
										</h4>
									</div>
									<div>
										<img src="../assets/images/expand.png" onClick={handleClose1} style={{ cursor: 'pointer' }} />
									</div>
								</div>

								{vectorAnalytics && (vectorAnalytics.drilldownChart?.length > 0 || vectorAnalytics.columnChart?.length > 0) ? (
									<>
										<h3 className="legend-title">
											Top 10 {admDesc[`ADM${vectorAnalytics.admX}`] || 'Regions'} - {vectorAnalytics.layerName || 'Data'}
											{vectorAnalytics.units ? ` (${vectorAnalytics.units})` : ''}
										</h3>
										{vectorAnalytics.drilldownChart?.length > 0 && (
											<div>
												<button className="btn playBtn me-2" onClick={handleButtonClick}>
													{isPlaying ? (
														<i className="fas fa-circle-pause text-white"></i>
													) : (
														<i className="fas fa-circle-play text-white"></i>
													)}
												</button>
												<input
													type="range"
													id="play-range"
													min={startYear}
													max={endYear}
													value={year || endYear}
													onChange={handleRangeChange}
													style={{ width: '80%' }}
												/>
											</div>
										)}
										<div style={{ height: '450px', position: 'relative', zIndex: 1000 }}>
											<HighchartsReact
												ref={chartRef}
												highcharts={Highcharts}
												options={optionsColumnChart(vectorAnalytics, vectorAnalytics.drilldownChart?.length > 0 ? year : null)}
											/>
										</div>
									</>
								) : (
									<p style={{ textAlign: 'center', color: '#333', marginTop: '20px' }}>
										No data available.
									</p>
								)}
							</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</>
	);
};

export default Product2Legend;